import Moment from 'react-moment';
import { Link, useHistory } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';
import key from 'weak-key';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { tokens } from '@treams/treams-ui';

const LearningCalendarDayElement = styled(Box)<{ isSelectedMonth?: boolean }>`
	color: ${(props) => (props.isSelectedMonth ? tokens.text.primary : tokens.text.tertiary)};
	min-height: 100px;
	border-right: 1px solid ${tokens.border.subtle};

	:last-child {
		border-right-width: 0;
	}
`;

const LearningCalendarEventElement = styled(Link)<{
	tintColor: string;
	isRSVP: boolean;
	cancelled: boolean;
}>`
	display: block;
	border-radius: ${tokens.radius.xs}px;
	border: 1px solid ${(props) => props.tintColor};
	font-size: ${tokens.typography.sizes.small}px;
	padding: 3px 4px;
	margin-bottom: ${tokens.gap.inline}px;
	${(props) => (props.cancelled ? 'opacity: 0.4;' : '')}
	:last-child {
		margin-bottom: 0;
	}

	${(props) =>
		props.isRSVP
			? `
			background-color: ${props.tintColor};
			color: ${tokens.text.inverse};
		`
			: `
			background-color: ${tokens.surface.white};
			color: ${props.tintColor};
	`}
`;

const LearningCalendarEventTime = styled.p`
	font-weight: ${tokens.typography.weights.bold};
	font-size: 10px;
	padding-bottom: 0;
	text-align: right;
	display: block;
	margin-bottom: -3px;
`;

const LearningEventTitle = styled.span<{ cancelled: boolean }>`
	${(props) => (props.cancelled ? 'text-decoration: line-through;' : '')}
`;

const LearningCalendarEvent = ({ event }: { event: graphql.LearningEvent }) => {
	const { localize } = useLocalize();
	const history = useHistory();

	const cancelled = event.status === graphql.LearningEventStatus.Cancelled;

	return (
		<LearningCalendarEventElement
			to={{
				pathname: `/learning/event/${event.id}`,
				state: {
					backgroundLocation: history.location,
				},
			}}
			tintColor={event.learningType.color}
			isRSVP={event.isViewerAttending}
			cancelled={cancelled}
		>
			<LearningEventTitle cancelled={cancelled}>{localize(event.title)?.text}</LearningEventTitle>
			<LearningCalendarEventTime>
				<Moment format="HH:mm">{event.startDate}</Moment>-
				<Moment format="HH:mm">{event.endDate}</Moment>
			</LearningCalendarEventTime>
		</LearningCalendarEventElement>
	);
};

const LearningCalendarDay = ({
	date,
	month,
	events,
}: {
	date: Date;
	month: number;
	events: graphql.LearningEvent[];
}) => {
	return (
		<LearningCalendarDayElement
			flexGrow={1}
			flexBasis={1}
			p={2}
			isSelectedMonth={date.getMonth() === month - 1}
		>
			{date.getDate()}

			{events.map((event) => (
				<LearningCalendarEvent key={key(event)} event={event} />
			))}
		</LearningCalendarDayElement>
	);
};

export default LearningCalendarDay;
